import { AiKey } from "./aiTokenLimit";

export interface AiMessage {
    id: number;
    user_id: number; // 0 = AI, user.id = user
    text: string;
    created_at: string;
    isTyping?: boolean;
    idNotSend?: boolean;
}

export interface AiConversation {
    id: string;            // uuid-like: timestamp + random
    ai: AiKey;
    title: string;         // first user message (truncated)
    created_at: string;
    updated_at: string;
    messages: AiMessage[];
    is_pinned?: boolean;
    pinned_at?: string;
}

const STORAGE_KEY = (userId: number | undefined) => `ai_conversations_${userId}`;
export const MAX_CONVERSATIONS = 50;

function genId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getConversationTime(conversation: AiConversation): number {
    const value = conversation.is_pinned
        ? conversation.pinned_at || conversation.updated_at || conversation.created_at
        : conversation.updated_at || conversation.created_at;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
}

/** Pinned conversations first, then the most recently updated conversations. */
export function sortConversations(list: AiConversation[]): AiConversation[] {
    return [...list].sort((a, b) => {
        const aPinned = Boolean(a.is_pinned);
        const bPinned = Boolean(b.is_pinned);

        if (aPinned !== bPinned) return aPinned ? -1 : 1;
        return getConversationTime(b) - getConversationTime(a);
    });
}

function normalizeConversation(value: unknown): AiConversation | null {
    if (!value || typeof value !== "object") return null;
    const conversation = value as Partial<AiConversation>;
    if (
        typeof conversation.id !== "string" ||
        (conversation.ai !== "pacoca" && conversation.ai !== "bochecha") ||
        typeof conversation.title !== "string" ||
        typeof conversation.created_at !== "string" ||
        typeof conversation.updated_at !== "string" ||
        !Array.isArray(conversation.messages)
    ) {
        return null;
    }

    return {
        ...conversation,
        messages: conversation.messages,
        is_pinned: Boolean(conversation.is_pinned),
        pinned_at: conversation.is_pinned ? conversation.pinned_at : undefined,
    } as AiConversation;
}

export function loadConversations(userId: number | undefined): AiConversation[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY(userId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return sortConversations(
            parsed
                .map(normalizeConversation)
                .filter((conversation): conversation is AiConversation => conversation !== null),
        ).slice(0, MAX_CONVERSATIONS);
    } catch {
        return [];
    }
}

function saveConversations(userId: number | undefined, list: AiConversation[]): void {
    try {
        const trimmed = sortConversations(list).slice(0, MAX_CONVERSATIONS);
        localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(trimmed));
    } catch { }
}

export function canStartNewAiConversation(messages: AiMessage[]): boolean {
    return messages.length > 0;
}

export function createConversation(userId: number | undefined, ai: AiKey): AiConversation {
    const now = new Date().toISOString();
    const conv: AiConversation = {
        id: genId(),
        ai,
        title: "Nova conversa",
        created_at: now,
        updated_at: now,
        messages: [],
        is_pinned: false,
    };
    const existing = loadConversations(userId);
    saveConversations(userId, [conv, ...existing]);
    return conv;
}

export function getConversation(userId: number | undefined, id: string): AiConversation | null {
    return loadConversations(userId).find(c => c.id === id) ?? null;
}

export function updateConversation(
    userId: number | undefined,
    id: string,
    patch: Partial<Pick<AiConversation, "title" | "messages" | "updated_at">>
): void {
    const list = loadConversations(userId);
    const idx = list.findIndex(c => c.id === id);
    if (idx === -1) return;

    list[idx] = {
        ...list[idx],
        ...patch,
        updated_at: patch.updated_at ?? new Date().toISOString(),
    };
    saveConversations(userId, list);
}

export function togglePinConversation(
    userId: number | undefined,
    id: string,
): AiConversation[] {
    const list = loadConversations(userId);
    const now = new Date().toISOString();
    const updated = list.map(conversation => {
        if (conversation.id !== id) return conversation;
        const isPinned = !conversation.is_pinned;
        return {
            ...conversation,
            is_pinned: isPinned,
            pinned_at: isPinned ? now : undefined,
        };
    });
    saveConversations(userId, updated);
    return loadConversations(userId);
}

export function deleteConversation(userId: number | undefined, id: string): void {
    const list = loadConversations(userId).filter(c => c.id !== id);
    saveConversations(userId, list);
}

/** Derive a title from the first user message */
export function deriveTitleFromMessage(text: string): string {
    const clean = text.replace(/\s+/g, " ").trim();
    if (clean.length <= 40) return clean;
    return clean.slice(0, 37) + "…";
}

/** Returns the active conversation id stored for this session */
const ACTIVE_KEY = (userId: number | undefined) => `ai_active_conv_${userId}`;

export function getActiveConversationId(userId: number | undefined): string | null {
    try { return localStorage.getItem(ACTIVE_KEY(userId)); } catch { return null; }
}

export function setActiveConversationId(userId: number | undefined, id: string | null): void {
    try {
        if (id === null) localStorage.removeItem(ACTIVE_KEY(userId));
        else localStorage.setItem(ACTIVE_KEY(userId), id);
    } catch { }
}
